import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER, NEAR_BLACK, GRAY_DARK, BLUE_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import AddIcon from 'ui-library/icons/AddIcon';

interface AddViewButtonProps {
  onClick: () => void;
  disabledReason: string | null;
}

/** @package */
export default function AddViewButton({onClick, disabledReason}: AddViewButtonProps): JSX.Element {
  const disabled = disabledReason != null;
  const [isHovered, hoverProps] = useHoverState();
  return (
    <Tooltip text={disabledReason} isDisabled={!disabled}>
      <div
        className={css(styles.wrapper, disabled ? styles.disabledWrapper : styles.enabledWrapper)}
        onClick={() => {
          if (!disabled) {
            onClick();
          }
        }}
        {...hoverProps}
        {...getTestProps({
          component: 'AddViewButton',
          element: 'root',
        })}
      >
        <AddIcon
          // AddIcon by default adds `u-small-spacing-left`, but we don't want that spacing here. Make sure
          // className is always defined, even if only as an empty string.
          className={css(
            styles.icon,
            isHovered && !disabled && styles.hoveredEnabledIcon,
            disabled && styles.disabledIcon
          )}
          disabled={disabled}
        />
        <span className="u-mini-spacing-left">New view</span>
      </div>
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    ...FONT_PROXIMA_13,
    display: 'flex',
    alignItems: 'center',
  },
  enabledWrapper: {
    color: GRAY_DARKER,
    ':hover': {
      fontWeight: 'bold',
      color: NEAR_BLACK,
    },
    cursor: 'pointer',
  },
  disabledWrapper: {
    color: GRAY_DARK,
    cursor: 'not-allowed',
  },
  icon: {
    color: BLUE_LIGHT,
    padding: 2,
  },
  disabledIcon: {
    color: GRAY_DARK,
  },
  hoveredEnabledIcon: {
    borderRadius: 4,
    background: GRAY_LIGHTER,
  },
});
